import styled from 'styled-components'
import { grey } from '@/theme/colors'
import { cardSurface } from '@components/Card'
import { media } from '@/theme/tokens'

/**
 * One continuous card of three panes split by hairlines: the trip summary, the
 * calendar and the legend on the left, the selected day in the middle, the map
 * on the right. Above the tabletWide breakpoint it runs to the bottom of the
 * viewport, floored at what the left column needs at its tallest — a six-week
 * month over two legend rows and their pager — so nothing there is ever clipped
 * on a short screen (the page scrolls then). Below that three columns do not
 * fit, so the card stacks and grows with its content: the summary over the
 * calendar beside the legend, the day, the map last at a fixed height.
 *
 * The columns keep their 28 / 40 / 32 split up to the shell's width, with the
 * calendar floored at 280px and the map at 260px so the day still has a phone's
 * width just past the breakpoint. Past the shell the calendar stops at 400px —
 * its day cells are square, so it grows taller as it grows wider — and the day
 * at 640px, a readable line; the map takes everything else.
 */
export const ItineraryCard = styled.div`
	${cardSurface}
	padding: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	${media.aboveTabletWide} {
		display: grid;
		grid-template-columns: clamp(280px, 28%, 400px) minmax(0, 640px) minmax(max(260px, 32%), 1fr);
		/* One row, as tall as the card: without it the row sizes to content and the panes stop scrolling. */
		grid-template-rows: minmax(0, 1fr);
		flex: 1 1 0;
		min-height: 620px;
	}
`

export const CalendarColumn = styled.div`
	flex: none;
	padding: 18px 24px 16px;
	border-bottom: 1px solid ${grey};
	display: flex;
	/* The legend drops under the calendar when the row has no room for both. */
	flex-wrap: wrap;
	gap: 16px 28px;
	align-items: flex-start;

	/* Its own column: the summary, the calendar at its height, the legend paging through what is left under it. */
	${media.aboveTabletWide} {
		flex-direction: column;
		flex-wrap: nowrap;
		align-items: stretch;
		gap: 16px;
		min-height: 0;
		overflow: hidden;
		border-bottom: none;
		border-right: 1px solid ${grey};
	}

	${media.phone} {
		padding: 16px;
	}
`

export const DayColumn = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;

	${media.aboveTabletWide} {
		min-height: 0;
		overflow: hidden;
	}
`

export const MapColumn = styled.div`
	position: relative;
	flex: none;
	height: 360px;
	border-top: 1px solid ${grey};

	${media.aboveTabletWide} {
		height: auto;
		min-height: 0;
		border-top: none;
		border-left: 1px solid ${grey};
	}
`
